import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, CalendarClock, Users, Ban } from "lucide-react";

const SPORTS = ["ENDURO", "4X4", "MOUNTAIN BIKE", "TREKKING"];

const emptyForm = {
  nome: "",
  cognome: "",
  whatsapp: "",
  email: "",
  attivita_sportiva: [],
  eventi: ["", "", "", "", ""],
};

function Field({ label, children, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="flex flex-col gap-2"
    >
      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</Label>
      {children}
    </motion.div>
  );
}

export default function RegistrationForm() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [seats, setSeats] = useState(null);

  const fetchCount = async () => {
    try {
      const { data } = await api.get("/registrations/count");
      setSeats(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchCount();
  }, []);

  const inputCls =
    "bg-background text-foreground placeholder:text-muted-foreground border-foreground/15 h-12 rounded-lg focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary transition-colors";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSport = (s) =>
    setForm((f) => ({
      ...f,
      attivita_sportiva: f.attivita_sportiva.includes(s)
        ? f.attivita_sportiva.filter((x) => x !== s)
        : [...f.attivita_sportiva, s],
    }));
  const setEvento = (i, v) =>
    setForm((f) => {
      const eventi = [...f.eventi];
      eventi[i] = v;
      return { ...f, eventi };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.whatsapp || !form.email || form.attivita_sportiva.length === 0) {
      toast.error("Compila tutti i campi obbligatori e scegli almeno un'attività.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/registrations", form);
      setDone(true);
      toast.success("Iscrizione registrata! Ci vediamo al corso.");
      setForm(emptyForm);
      fetchCount();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante l'iscrizione.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="iscrizione" className="py-24 md:py-40 bg-[#1A2F24] text-[#F4F1ED]" data-testid="registration-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <p className="text-primary uppercase tracking-[0.3em] text-xs font-semibold mb-6">Iscrizione gratuita</p>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.9]">
            Prenota il<br />tuo posto.
          </h2>
          <p className="font-serif-editorial italic text-2xl mt-8 text-[#F4F1ED]/70 max-w-sm">
            Compila il modulo: ti contatteremo su WhatsApp con tutti i dettagli. Riceverai anche i file per esercitarti durante il corso.
          </p>
          <div className="mt-8 flex items-start gap-4 border border-[#F4F1ED]/20 rounded-2xl p-6 bg-white/[0.04] max-w-md" data-testid="dates-info">
            <CalendarClock className="w-6 h-6 text-primary shrink-0 mt-1" strokeWidth={2} />
            <div className="text-sm text-[#F4F1ED]/80 leading-relaxed">
              <p>Il corso si terrà in <strong>due serate alle ore 21:00</strong>, il <strong>2 e il 3 settembre</strong>.</p>
              <p className="mt-2">Iscrizioni a <strong>numero limitato</strong>: chiudono il <strong>20 agosto</strong>.</p>
              <p className="mt-2">Chi confermerà la partecipazione riceverà i <strong>file da utilizzare durante il corso</strong> via email o WhatsApp.</p>
            </div>
          </div>

          {seats && (
            <div className="mt-6 max-w-md" data-testid="seats-counter">
              <div className="flex items-end justify-between mb-2">
                <span className="font-display font-black text-4xl leading-none flex items-baseline gap-1">
                  <Users className="w-6 h-6 text-primary self-center" strokeWidth={2.5} />
                  {seats.count}
                  <span className="text-[#F4F1ED]/40 text-2xl">/{seats.max}</span>
                </span>
                <span className={`text-xs uppercase tracking-[0.2em] font-semibold ${seats.full ? "text-primary" : "text-[#F4F1ED]/60"}`} data-testid="seats-remaining">
                  {seats.full ? "Posti esauriti" : `${seats.remaining} posti rimasti`}
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-primary transition-[width] duration-700 ease-out"
                  style={{ width: `${Math.min((seats.count / seats.max) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-7 w-full">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="border border-[#F4F1ED]/20 rounded-[2rem] p-12 md:p-16 bg-white/5"
              data-testid="registration-success"
            >
              <CheckCircle2 className="w-14 h-14 text-primary" strokeWidth={1.5} />
              <h3 className="font-display font-extrabold text-3xl mt-6">Sei dentro! 🎒</h3>
              <p className="text-[#F4F1ED]/70 mt-4 text-lg max-w-md">
                La tua iscrizione è stata registrata con successo. Il corso si terrà il <strong>2 e il 3 settembre alle ore 21:00</strong>: riceverai i dettagli e i file da utilizzare via email o WhatsApp dopo la conferma.
              </p>
              <Button
                onClick={() => { setDone(false); fetchCount(); }}
                variant="outline"
                className="mt-8 rounded-full bg-transparent border-[#F4F1ED]/30 text-[#F4F1ED] hover:bg-[#F4F1ED] hover:text-[#1A2F24]"
                data-testid="new-registration-btn"
              >
                Iscrivi un'altra persona
              </Button>
            </motion.div>
          ) : seats?.full ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="border border-primary/40 rounded-[2rem] p-12 md:p-16 bg-white/5"
              data-testid="registration-soldout"
            >
              <Ban className="w-14 h-14 text-primary" strokeWidth={1.5} />
              <h3 className="font-display font-extrabold text-3xl mt-6">Posti esauriti</h3>
              <p className="text-[#F4F1ED]/70 mt-4 text-lg max-w-md">
                Abbiamo raggiunto i <strong>300 iscritti</strong> e le iscrizioni sono chiuse. Seguici su Offroad Italia per non perdere le prossime edizioni del corso.
              </p>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              className="border border-[#F4F1ED]/15 rounded-[2rem] p-8 md:p-12 bg-white/[0.03] backdrop-blur-sm space-y-6"
              data-testid="registration-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Nome *" index={0}>
                  <Input data-testid="input-nome" className={inputCls} value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Marco" />
                </Field>
                <Field label="Cognome *" index={1}>
                  <Input data-testid="input-cognome" className={inputCls} value={form.cognome} onChange={(e) => set("cognome", e.target.value)} placeholder="Rossi" />
                </Field>
                <Field label="Numero WhatsApp *" index={2}>
                  <Input data-testid="input-whatsapp" type="tel" className={inputCls} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+39 333 1234567" />
                </Field>
                <Field label="Email *" index={3}>
                  <Input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="marco@email.it" />
                </Field>
              </div>

              <Field label="Attività praticate * (selezione multipla)" index={4}>
                <div className="flex flex-wrap gap-2" data-testid="sport-options">
                  {SPORTS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => toggleSport(s)}
                      data-testid={`sport-${s}`}
                      className={`rounded-full px-4 py-2 text-sm font-semibold tracking-wide border transition-colors ${
                        form.attivita_sportiva.includes(s)
                          ? "bg-primary border-primary text-white"
                          : "border-[#F4F1ED]/25 text-[#F4F1ED]/80 hover:border-[#F4F1ED]/60"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Eventi a cui hai partecipato (facoltativo, fino a 5)" index={5}>
                <div className="grid grid-cols-1 gap-3">
                  {form.eventi.map((ev, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="font-display font-bold text-[#F4F1ED]/40 w-6 shrink-0">{i + 1}</span>
                      <Input
                        data-testid={`input-evento-${i}`}
                        className={inputCls}
                        value={ev}
                        onChange={(e) => setEvento(i, e.target.value)}
                        placeholder={i === 0 ? "es. Enduring dei Vulcani" : "Nome evento"}
                      />
                    </div>
                  ))}
                </div>
              </Field>

              <Button
                type="submit"
                disabled={loading}
                data-testid="submit-registration-btn"
                className="w-full rounded-full h-14 bg-primary hover:bg-primary/90 text-primary-foreground uppercase tracking-[0.2em] text-xs font-semibold"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Conferma iscrizione gratuita"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
